import { Helmet } from "react-helmet-async";
import { Link, Navigate, useParams } from "react-router-dom";
import { useAppState } from "../context/AppContext";
import { usePractice } from "../usePractice";
import { useIsPracticeAdmin } from "../useIsPracticeAdmin";
import { usePracticeSettings } from "../usePracticeSettings";
import { getBrand } from "../utils/brand";
import { CONTROL_GROUPS, getControlValue } from "../config/practiceControls";
import ToggleSwitch from "../ui/ToggleSwitch";

function AdminControl({ group, control, value, disabled, onChange }) {
  const controlId = `${group.id}-${control.key}`;
  return (
    <div className="admin-control" data-cy={`control-${control.key}`}>
      <div className="admin-control-copy">
        <label htmlFor={controlId} className="base-bold admin-control-label">
          {control.label}
        </label>
        <p className="admin-control-description">{control.description}</p>
      </div>
      <div className="admin-control-action">
        <span className="admin-control-state">{value ? "On" : "Off"}</span>
        <ToggleSwitch
          id={controlId}
          checked={value}
          disabled={disabled}
          ariaLabel={control.label}
          onChange={(next) =>
            onChange({
              storageKey: group.storageKey,
              key: control.key,
              value: next,
            })
          }
        />
      </div>
    </div>
  );
}

function PracticeAdmin() {
  const { slug } = useParams();
  const { state } = useAppState();
  const { practice, isLoadingPractice } = usePractice();
  const { isAdmin, isReady } = useIsPracticeAdmin();
  const brand = getBrand(practice?.fields?.brand);

  const practiceName = practice?.fields?.practiceName;
  const {
    settings,
    isLoading: isLoadingSettings,
    error,
    updateSetting,
    isSaving,
    saveError,
  } = usePracticeSettings(slug, practiceName);

  // Wait for auth + role data before deciding anything, so we don't flash a
  // redirect while the user's practices are still loading.
  if (!state.authChecked || !isReady) {
    return <div className="loading-screen regular-2xl">Loading...</div>;
  }

  // PrivateRoute already guarantees a signed-in user; this guards the role.
  if (!isAdmin) {
    return <Navigate to={`/${slug}`} replace />;
  }

  return (
    <div className="container">
      <Helmet>
        <title>{brand.name} - Practice Settings</title>
      </Helmet>

      <div className="container-inner">
        <div className="admin-header">
          <div>
            <h2>Practice Settings</h2>
            <p className="admin-subtitle">{practiceName} admin controls</p>
          </div>
          <Link className="link" to={`/${slug}`}>
            ← Back to cases
          </Link>
        </div>

        {(isLoadingPractice || isLoadingSettings) && (
          <div className="loading-screen regular-2xl">Loading...</div>
        )}

        {error && (
          <p className="input-error">
            Could not load settings. Please refresh and try again.
          </p>
        )}

        {!isLoadingSettings && !error && (
          <>
            {saveError && (
              <p className="input-error" role="alert">
                Something went wrong saving your change. Please try again.
              </p>
            )}

            {CONTROL_GROUPS.map((group) => (
              <section
                key={group.id}
                className="admin-section"
                data-cy={`group-${group.id}`}
              >
                <div className="admin-section-heading">
                  <h3>{group.title}</h3>
                  {group.description && (
                    <p className="admin-section-description">
                      {group.description}
                    </p>
                  )}
                </div>

                <div className="admin-control-list">
                  {group.controls.map((control) => (
                    <AdminControl
                      key={control.key}
                      group={group}
                      control={control}
                      value={getControlValue(settings, group, control)}
                      disabled={isSaving || control.disabled}
                      onChange={updateSetting}
                    />
                  ))}
                </div>
              </section>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

export default PracticeAdmin;
